/**
 * Dashboard → "Share your stats" (FR-011, US4: "share a read-only link with
 * their VP"). Creates `/share/:token` links that open the funnel with no
 * sign-in. Named for the numbers (owner feedback 2026-09-27: "Share a
 * read-only view" read as possibly sharing the widget); the widget itself is
 * shared by the separate "Share your widget page" panel (WidgetPageShare.tsx). Each link expires (7, 30 or 90 days) and can be revoked; the page
 * carries counts only, never a lead or a contact.
 *
 * Scope (D-3, migration 065): the partner picks what the link shows when
 * creating it — the period (last 7, 30 or 90 days, rolling) and optionally one
 * website and/or one trade (US4's "by origin and trade"). The scope is stored
 * on the link; whoever opens it cannot widen it. The list says what each link
 * shows, so an admin can tell "the Seattle site, plumbing only" from
 * "everything".
 *
 * The token is shown once, right after creation — the list is metadata only,
 * so it cannot be lifted from here later. The link is built on this portal's
 * own origin, so a link made on the internal tier opens the internal tier.
 */
import { useState } from 'react';
import type { ShareLinkCreated, ShareLinkScope } from '../api/types';
import { can } from '../auth/roles';
import { useSession } from '../auth/session';
import { TRADES, tradeLabel } from '../config/model';
import { CopyButton, ErrorNotice, Field, Loading, Time, formatDateTime, useToast } from '../ui/kit';
import { useMutation, usePhone, useQuery } from '../ui/hooks';

type Days = '7' | '30' | '90';

export function shareUrl(token: string): string {
  return `${window.location.origin}/share/${encodeURIComponent(token)}`;
}

export { tradeLabel };

/**
 * "Last 30 days · all websites · all trades" — one line saying what a link
 * shows. Links made before migration 065 have no `scope` and showed 30 days,
 * unfiltered.
 */
export function describeScope(scope: ShareLinkScope | undefined): string {
  const s = scope ?? { period_days: 30, origin: null, trade: null };
  return [
    `Last ${s.period_days} days`,
    s.origin ? s.origin.replace(/^https?:\/\//, '') : 'all websites',
    s.trade ? tradeLabel(s.trade) : 'all trades',
  ].join(' · ');
}

export function ShareLinks() {
  const { api, role } = useSession();
  const toast = useToast();
  const phone = usePhone();
  const [expiry, setExpiry] = useState<Days>('30');
  const [period, setPeriod] = useState<Days>('30');
  const [origin, setOrigin] = useState('');
  const [trade, setTrade] = useState('');
  const [fresh, setFresh] = useState<ShareLinkCreated | null>(null);
  const links = useQuery(() => api.listShareLinks(), []);
  // The websites the widget has been seen on (every site of the org), for the
  // origin filter. A failed read only costs the filter its options.
  const health = useQuery(() => api.installHealth(), [], { enabled: can(role, 'installHealth') });
  const origins = [...new Set((health.data?.origins ?? []).map((o) => o.origin))].sort();
  const create = useMutation(() =>
    api.createShareLink({
      expires_in_days: Number(expiry),
      period_days: Number(period) as ShareLinkScope['period_days'],
      ...(origin ? { origin } : {}),
      ...(trade ? { trade } : {}),
    }),
  );
  // `true` rather than the DELETE's empty body, so success is distinguishable
  // from useMutation's `undefined` on failure.
  const revoke = useMutation(async (id: string) => {
    await api.revokeShareLink(id);
    return true as const;
  });

  const revokeButton = (l: { id: string; created_at: string }, i: number) => (
    <button
      type="button"
      className="btn btn-sm btn-ghost"
      // Unique per row (#888 review finding 9): two links made in the same
      // minute used to share one label. The position makes it unique; the
      // seconds make it recognisable.
      aria-label={`Revoke link ${i + 1}, created ${formatDateTime(l.created_at, { seconds: true })}`}
      disabled={revoke.pending}
      onClick={async () => {
        if (!window.confirm('Revoke this link? Anyone using it will lose access immediately.')) return;
        const done = await revoke.run(l.id);
        if (done) {
          if (fresh?.id === l.id) setFresh(null);
          toast('Link revoked');
          void links.reload();
        }
      }}
    >
      Revoke
    </button>
  );

  return (
    <section className="panel" aria-labelledby="share-links-title">
      <div className="panel-head">
        <div className="stack-sm" style={{ gap: 2 }}>
          <h2 id="share-links-title">Share your stats</h2>
          <span className="small muted">
            A private link to this dashboard’s numbers — no leads, names or contact details. It doesn’t share your widget.
          </span>
        </div>
      </div>
      <div className="panel-body stack">
        <p className="small muted" style={{ margin: 0 }}>
          No sign-in needed to open it, and whoever has it can’t change the period or filters you pick here.
        </p>
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const made = await create.run();
            if (made) {
              setFresh(made);
              void links.reload();
            }
          }}
        >
          <div className="grid-2">
            <Field label="Shows" hint="A rolling window that ends when the link is opened.">
              {(p) => (
                <select {...p} className="input" value={period} onChange={(e) => setPeriod(e.target.value as Days)}>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </select>
              )}
            </Field>
            <Field label="Link expires after">
              {(p) => (
                <select {...p} className="input" value={expiry} onChange={(e) => setExpiry(e.target.value as Days)}>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                </select>
              )}
            </Field>
            <Field label="Website" hint={origins.length ? undefined : 'Websites appear here once the widget has loaded on them.'}>
              {(p) => (
                <select {...p} className="input" value={origin} onChange={(e) => setOrigin(e.target.value)}>
                  <option value="">All websites</option>
                  {origins.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Trade">
              {(p) => (
                <select {...p} className="input" value={trade} onChange={(e) => setTrade(e.target.value)}>
                  <option value="">All trades</option>
                  {TRADES.map((t) => (
                    <option key={t.slug} value={t.slug}>
                      {t.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          <div>
            <button type="submit" className="btn" disabled={create.pending}>
              {create.pending ? 'Creating…' : 'Create stats link'}
            </button>
          </div>
        </form>
        <ErrorNotice error={create.error ?? revoke.error ?? links.error} />
        {fresh ? (
          <div className="sunk stack-sm" role="status">
            <div className="keyline">
              <code className="mono wrap-anywhere">{shareUrl(fresh.token)}</code>
              <CopyButton text={shareUrl(fresh.token)} label="Copy link" />
            </div>
            <span className="small muted">
              <strong>{describeScope(fresh.scope)}</strong>. Copy it now — we only show a link once. It expires{' '}
              {formatDateTime(fresh.expires_at)}.
            </span>
          </div>
        ) : null}
        {links.loading ? (
          <Loading />
        ) : links.data?.items.length ? (
          phone ? (
            <div className="cards">
              {links.data.items.map((l, i) => (
                <div className="card" key={l.id}>
                  <strong style={{ color: 'var(--ink)' }}>{describeScope(l.scope)}</strong>
                  <span className="small muted">
                    Created <Time iso={l.created_at} />
                    {l.created_by ? ` · ${l.created_by}` : ''}
                  </span>
                  <span className="small muted">
                    Expires {formatDateTime(l.expires_at)} · viewed {l.last_viewed_at ? <Time iso={l.last_viewed_at} /> : 'never'}
                  </span>
                  <span className="card-actions">{revokeButton(l, i)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">Shows</th>
                    <th scope="col">Created</th>
                    <th scope="col">Expires</th>
                    <th scope="col">Last viewed</th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {links.data.items.map((l, i) => (
                    <tr key={l.id}>
                      <td>{describeScope(l.scope)}</td>
                      <td>
                        <Time iso={l.created_at} />
                        {l.created_by ? <span className="small muted"> · {l.created_by}</span> : null}
                      </td>
                      <td>{formatDateTime(l.expires_at)}</td>
                      <td>{l.last_viewed_at ? <Time iso={l.last_viewed_at} /> : <span className="muted">Never</span>}</td>
                      <td style={{ textAlign: 'right' }}>{revokeButton(l, i)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <p className="small muted" style={{ margin: 0 }}>
            No active stats links.
          </p>
        )}
      </div>
    </section>
  );
}
